/**
 * Pick the teammates who get @mentioned on a Slack notification.
 *
 * Selects ERP profiles, not Slack handles. The backend resolves each profile's
 * email to a Slack member id at send time (`lib/slack-mentions.ts`), because a
 * literal `@grace` typed into a message body notifies nobody — only the
 * `<@U…>` form does. Choosing people instead of handles also means a teammate
 * changing their Slack display name never silently stops being pinged.
 *
 * Extracted as a shared component because three surfaces now need the same
 * control (OOS digest, filed print-file alerts, Portless blocked-order digest)
 * and the first of them had grown it inline.
 */
import { useMemo } from 'react';
import { UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { trpc } from '@/lib/trpc';

/** Mirrors MAX_MENTION_RECIPIENTS on the backend and the DB-side array cap. */
export const MAX_MENTION_RECIPIENTS = 25;

interface SlackMentionPickerProps {
  /** Selected ERP profile UUIDs. */
  value: string[];
  onChange: (next: string[]) => void;
  /** Skip the member fetch until the parent feature is switched on. */
  enabled?: boolean;
  disabled?: boolean;
  /** Hint shown inside the popover — surface-specific context. */
  hint?: string;
}

export function SlackMentionPicker({
  value,
  onChange,
  enabled = true,
  disabled = false,
  hint,
}: SlackMentionPickerProps) {
  const membersQuery = trpc.organization.getMembers.useQuery(undefined, { enabled });

  const activeMembers = useMemo(
    () =>
      (membersQuery.data ?? [])
        .filter((member) => member.isActive && member.email)
        .map((member) => ({
          id: member.id,
          label: [member.firstName, member.lastName].filter(Boolean).join(' ') || member.email,
          email: member.email,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [membersQuery.data],
  );

  const toggle = (profileId: string) => {
    if (!value.includes(profileId) && value.length >= MAX_MENTION_RECIPIENTS) {
      toast.error(`You can notify up to ${MAX_MENTION_RECIPIENTS} teammates`);
      return;
    }
    onChange(
      value.includes(profileId) ? value.filter((id) => id !== profileId) : [...value, profileId],
    );
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          className="w-full justify-start sm:w-80"
        >
          <UserPlus className="mr-2 h-4 w-4" aria-hidden="true" />
          {value.length > 0
            ? `${value.length} teammate${value.length === 1 ? '' : 's'} selected`
            : 'Select teammates'}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-2">
        <p className="px-2 pb-2 text-xs text-muted-foreground">
          {hint ?? 'Selected teammates are @mentioned when their PrintRun email matches Slack.'}
        </p>
        <div className="max-h-64 space-y-0.5 overflow-y-auto">
          {membersQuery.isLoading && (
            <p className="px-2 py-3 text-sm text-muted-foreground">Loading teammates…</p>
          )}
          {!membersQuery.isLoading && activeMembers.length === 0 && (
            <p className="px-2 py-3 text-sm text-muted-foreground">No active teammates found.</p>
          )}
          {activeMembers.map((member) => (
            <label
              key={member.id}
              className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-muted"
            >
              <Checkbox checked={value.includes(member.id)} onCheckedChange={() => toggle(member.id)} />
              <span className="min-w-0 flex-1 truncate">{member.label}</span>
              <span className="max-w-36 truncate text-xs text-muted-foreground">{member.email}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
